import type {TankGaugeReading_AllWhereQuery} from '@/graphql/generated/graphql.ts';
import type {TankGaugeReading} from '@/types/well';
import {deterministicUuidFromSeed} from '@/lib/util.ts';

type TankGaugeReadingRow = NonNullable<
   NonNullable<TankGaugeReading_AllWhereQuery['tankGaugeReading_AllWhere']>[number]
>;

// gaugeDate comes back typed `unknown` (DateOnly has no scalar mapping in
// codegen.yml, same as every other custom scalar here) — same idiom as
// lib/pulledBottomMapping.ts's pulledDateOf/lib/barrelTestMapping.ts's
// testDateOf.
function gaugeDateOf(row: TankGaugeReadingRow): string {
   return row.gaugeDate ? String(row.gaugeDate) : '';
}

/**
 * One reading per tank per day, shared by every well on that tank's
 * lease — the client always upserts by this deterministic id (derived
 * from tankId + gaugeDate) rather than a fresh generateUUID(), so
 * whichever well's report gets submitted first for a shared tank creates
 * the row, and a later well editing the same day's gauge updates that
 * same row instead of creating a duplicate. Same "converge on one row"
 * trick already used for Well Down/Restart — see
 * dailyReportTypes.ts's dailyWellReportStatusFlipId.
 */
export function deterministicTankGaugeReadingId(tankId: string, gaugeDate: string): string {
   return deterministicUuidFromSeed(`tank-gauge-reading:${tankId}:${gaugeDate}`);
}

/**
 * Calculated oil production (bbls) from a day-over-day gauge change:
 * (today's gauge − the prior recorded gauge) × the tank's `multiplier`
 * field (bbl/inch conversion factor — reusing Tank.multiplier rather
 * than a new field, same repurposing decision PulledBottom's chunk made
 * — see lib/pulledBottomMapping.ts's computeBblsPulled). Rounds to 2
 * decimal places, matching that same convention.
 *
 * Deliberately the RAW delta — chunk 4 scope (Duane, 2026-09-30): a day a
 * truck pulls oil or a run ticket empties the tank will make this number
 * look artificially low, and that's intentional for now. Reconciling
 * same-day PulledBottom/RunTicket volume back into this delta is
 * explicitly deferred to chunk 7's unexplained-drop flag, not solved
 * here — see that chunk's spec once it exists.
 */
export function computeOilProductionFromGaugeDelta(
   priorGaugeInches: number,
   todayGaugeInches: number,
   tankMultiplier: number,
): number {
   return Math.round((todayGaugeInches - priorGaugeInches) * tankMultiplier * 100) / 100;
}

/**
 * First-ever gauge reading for a tank (no prior TankGaugeReading to diff
 * against — a brand-new tank, or a gap of missed days before this
 * feature existed): derives an implied first-day production from the
 * tank's own recorded `startingBalance` (bbls) instead, treating it as
 * the baseline volume — (today's gauge × multiplier) − startingBalance.
 * Duane's call (2026-09-30), same "use what's already on the tank"
 * reasoning as reusing `multiplier` itself. Same 2-decimal rounding as
 * computeOilProductionFromGaugeDelta.
 */
export function computeOilProductionFromStartingBalance(
   todayGaugeInches: number,
   tankMultiplier: number,
   startingBalance: number,
): number {
   return Math.round((todayGaugeInches * tankMultiplier - startingBalance) * 100) / 100;
}

/** What the oil calculation needs from one tank entry (see WellTankGauge in useLeaseTankGauge.ts). */
export interface OilCalcTank {
   tank: {id: string; multiplier: number; startingBalance: number};
   priorReading: {gaugeInches: number} | null;
   wellAllocationPercent: number;
   hasValidAllocation: boolean;
}

/**
 * This well's share of one tank's oil for `gauge` inches, or null when there
 * isn't enough information to calculate it: no prior reading AND no usable
 * starting balance (a brand-new tank with nothing to diff against — Duane,
 * 2026-09-30, the pumper types Oil by hand then), or no multiplier. A
 * starting balance of 0 is treated as "not set" (the backend field is a
 * non-null Float, so unset and 0 look the same).
 */
export function tankOilBbls(t: OilCalcTank, gauge: number): number | null {
   if (!(t.tank.multiplier > 0)) return null;
   let tankBbls: number;
   if (t.priorReading) {
      tankBbls = computeOilProductionFromGaugeDelta(t.priorReading.gaugeInches, gauge, t.tank.multiplier);
   } else if (t.tank.startingBalance > 0) {
      tankBbls = computeOilProductionFromStartingBalance(gauge, t.tank.multiplier, t.tank.startingBalance);
   } else {
      return null;
   }
   return computeAllocatedOilProduction(tankBbls, t.wellAllocationPercent);
}

/**
 * Oil Production from the gauges entered so far: the sum of each tank's own
 * contribution. Tanks with no gauge yet, or not enough information to
 * calculate, are skipped; null when no tank contributed at all (leave the
 * Oil field alone).
 */
export function oilFromGauges(tanks: OilCalcTank[], gaugesByTankId: Record<string, number>): number | null {
   let total = 0;
   let contributed = false;
   tanks.forEach((t) => {
      const gauge = gaugesByTankId[t.tank.id];
      if (gauge == null) return;
      const bbls = tankOilBbls(t, gauge);
      if (bbls == null) return;
      total += bbls;
      contributed = true;
   });
   return contributed ? Math.round(total * 100) / 100 : null;
}

/**
 * Whether the Oil field should be read-only because the app calculates it.
 * Only for a field user (pumper), on a new OR already-saved report (a
 * pumper reopening their own report shouldn't be able to overwrite the
 * calculated value — changing a gauge recalculates it, see handleGaugeChange),
 * and only when every tank
 * has a gauge entered, is fully calculable and has a confirmed allocation.
 * Everything else stays manually editable: no gauge tank, a guessed
 * (equal-split) allocation, not enough information (see tankOilBbls), a
 * report already on file, and office/admin users.
 */
export function isOilLockedToGauge(
   tanks: OilCalcTank[],
   gaugesByTankId: Record<string, number>,
   opts: {isOfficeUser: boolean},
): boolean {
   if (opts.isOfficeUser || tanks.length === 0) return false;
   return tanks.every((t) => {
      const gauge = gaugesByTankId[t.tank.id];
      return gauge != null && t.hasValidAllocation && tankOilBbls(t, gauge) != null;
   });
}

/**
 * Maps real tankGaugeReading_AllWhere rows to the TankGaugeReading domain
 * shape. No denormalization needed (unlike toPulledBottoms) — every
 * consumer already has the tank in hand via its own tank lookup.
 */
export function toTankGaugeReadings(rows: (TankGaugeReadingRow | null)[] | null | undefined): TankGaugeReading[] {
   return (rows ?? [])
      .filter((row): row is TankGaugeReadingRow => row != null)
      .map((row) => ({
         id: String(row.id),
         tankId: row.tankId != null ? String(row.tankId) : '',
         gaugeDate: gaugeDateOf(row),
         gaugeInches: row.gaugeInches ?? 0,
         createdByUserId: row.createdByUserId != null ? String(row.createdByUserId) : '',
         createdDateTime: row.createdDateTime != null ? String(row.createdDateTime) : '',
         updatedByUserId: row.updatedByUserId != null ? String(row.updatedByUserId) : '',
         updatedDateTime: row.updatedDateTime != null ? String(row.updatedDateTime) : '',
      }));
}

/**
 * The most recent reading strictly before `date`, or null if none exists
 * — "strictly before" so re-opening today's own already-saved reading
 * (editing the same day again) diffs against yesterday, not against
 * itself. Readings are assumed already scoped to one tank by the caller
 * (see useLeaseTankGauge) — this just picks the latest one before
 * the cutoff out of that set.
 */
export function priorGaugeReading(readings: TankGaugeReading[], date: string): TankGaugeReading | null {
   const before = readings.filter((r) => r.gaugeDate < date);
   if (before.length === 0) return null;
   return before.reduce((latest, r) => (r.gaugeDate > latest.gaugeDate ? r : latest));
}

/** The reading already on file for `date` exactly, if any — used to prefill the gauge field when reopening an existing day. */
export function gaugeReadingForDate(readings: TankGaugeReading[], date: string): TankGaugeReading | null {
   return readings.find((r) => r.gaugeDate === date) ?? null;
}

/**
 * Chunk 5a (2026-09-30) — scales a tank-level daily bbl change down to
 * one well's share of a shared tank, per its allocation percentage (see
 * useLeaseTankGauge's wellAllocationPercent). Always a no-op (returns
 * `tankLevelBbls` unchanged, modulo the same 2-decimal rounding every
 * other calc here already applies) when `allocationPercent` is 100 — the
 * single-well-per-tank case chunk 4 originally handled.
 */
export function computeAllocatedOilProduction(tankLevelBbls: number, allocationPercent: number): number {
   return Math.round(tankLevelBbls * (allocationPercent / 100) * 100) / 100;
}

/**
 * Tank Gauge field helper text for both DailyReportPage.tsx (mobile) and
 * DailyReportPanel.tsx (desktop) — one shared implementation so the two
 * surfaces can't drift into describing the same state two different ways
 * (same "one source of truth" reasoning as dailyReportTypes.ts).
 *
 * Three independent things can each need a mention, so this composes them
 * rather than branching on every combination by hand:
 *   - wellCount <= 1: the trivial chunk-4 case, unchanged wording.
 *   - wellCount > 1 (chunk 5a, shared tank): says whether THIS well is
 *     the one required today (nobody's entered Tank {tankNumber} yet for
 *     this date) or already covered by another well on the same tank
 *     (existingReading already on file) — see the "first well reports,
 *     required; the rest see it pre-filled and editable" decision Duane
 *     made 2026-09-30. Chunk 6 (2026-09-30) is what actually enforces
 *     this wording — see requiresGaugeBeforeProduction below.
 *   - priorReadingExists === false: first-ever reading for this tank,
 *     regardless of wellCount — same starting-balance-derived estimate
 *     note chunk 4 always showed.
 *   - hasValidAllocation === false (only possible when wellCount > 1):
 *     flags that wellAllocationPercent is an equal-split guess, not a
 *     real report_TankAllocations/manual number — same "flag rather than
 *     silently guess" convention as lib/ogp03iOilBarrelsRuns.ts's
 *     wellsNeedingAllocationReview.
 */
export function tankGaugeHelperText(params: {
   wellCount: number;
   tankNumber: string;
   priorReadingExists: boolean;
   existingReadingExists: boolean;
   wellAllocationPercent: number;
   hasValidAllocation: boolean;
}): string {
   const {wellCount, tankNumber, priorReadingExists, existingReadingExists, wellAllocationPercent, hasValidAllocation} =
      params;
   const parts: string[] = [];

   if (wellCount <= 1) {
      parts.push(
         priorReadingExists
            ? 'Oil Production below is calculated from this gauge — you can still adjust it.'
            : "First reading on file for this tank — Oil Production is estimated from the tank's starting balance.",
      );
   } else {
      const pct = Math.round(wellAllocationPercent * 100) / 100;
      parts.push(
         existingReadingExists
            ? `Tank ${tankNumber} is shared by ${wellCount} wells — already entered today; this well's share is ${pct}%. You can still adjust the reading.`
            : `Tank ${tankNumber} is shared by ${wellCount} wells — required here since no one has entered today's gauge for it yet.`,
      );
      if (!priorReadingExists) {
         parts.push(
            "First reading on file for this tank — Oil Production is estimated from the tank's starting balance.",
         );
      }
      if (!hasValidAllocation) {
         parts.push(
            `⚠ No confirmed allocation on file for this well/tank — using an equal ${pct}% split across ${wellCount} wells until Allocation is set up.`,
         );
      }
   }

   return parts.join(' ');
}

/**
 * One tank's gauge-requirement status for chunk 6's missing-gauge
 * lockout — a minimal, decoupled shape (not the full WellTankGauge)
 * so this stays a pure lib function, same "loose params, no state-layer
 * import" convention as tankGaugeHelperText above.
 */
export interface TankGaugeCoverage {
   tankNumber: string;
   /** True once this tank is "covered" for the report being edited —
    * either a reading is already on file for this date (from this well
    * or another one sharing the tank) or the pumper has typed a value
    * into this session's own draft for it. */
   covered: boolean;
}

/**
 * Chunk 6 (2026-09-30) — the actual enforcement behind
 * tankGaugeHelperText's "required here" wording above (that function
 * only ever described the state; this one blocks on it). True when
 * production fields should stay locked until every qualifying tank on
 * this report has a gauge accounted for.
 *
 * Only ever true for a brand-new report (Duane's call, 2026-09-30, via
 * clarifying questions): gauge readings only started being recorded
 * with chunk 4, so nearly every pre-existing DailyWellReport has no
 * TankGaugeReading on file for its tank/date at all — reopening one of
 * those historical reports must never lock it, whatever its gauge
 * state. `hasExistingReport` is the same existingReport-not-null signal
 * both surfaces already compute for the date-change-conflict machinery
 * (see dailyReportTypes.ts's findReportForDate) — a report already on file is
 * grandfathered in regardless of date.
 *
 * For a genuinely new report, locks until every entry in `tanks` is
 * covered. A well with no qualifying tank at all (empty `tanks`, plain
 * manual entry) is never locked — there's no gauge field to require.
 * Callers are expected to also skip this while the well is being marked
 * Down — a Down submission claims no production, so nothing needs a
 * gauge to unlock it (see DailyReportPanel.tsx/DailyReportPage.tsx's own
 * `requiresGauge` derivation for exactly how that's folded in).
 */
export function requiresGaugeBeforeProduction(tanks: TankGaugeCoverage[], hasExistingReport: boolean): boolean {
   if (hasExistingReport) return false;
   return tanks.some((t) => !t.covered);
}

/**
 * The explanatory line shown near the (disabled) production fields while
 * requiresGaugeBeforeProduction is true — Duane's call (2026-09-30):
 * disable the fields, but say plainly what still needs to happen rather
 * than leaving them silently grayed out. Names the specific tank(s)
 * still missing, since the rare tank-overflow case (2+ tanks) can have
 * one covered and one not.
 */
export function missingGaugeMessage(tanks: TankGaugeCoverage[]): string {
   const missing = tanks.filter((t) => !t.covered).map((t) => t.tankNumber);
   if (missing.length === 0) return '';
   const label = missing.length === 1 ? `Tank ${missing[0]}` : `Tanks ${missing.join(', ')}`;
   return `Enter the gauge reading for ${label} above before entering production values.`;
}

/**
 * Tank gauges are read off the tank in feet and inches (tape-and-bob), but
 * stored — and multiplied by Tank.multiplier (bbl/inch) — as one total
 * number of inches, so the backend/calc side is unchanged. These two
 * helpers convert between the stored total and the two entry boxes.
 *
 * splitGaugeInches: 76.5 -> {feet: 6, inches: 4.5}. Inches are rounded to
 * 2 decimals first so float noise (e.g. 4.499999) never shows in the box,
 * and a round-up to a full 12 carries into feet.
 */
export function splitGaugeInches(totalInches: number): {feet: number; inches: number} {
   const rounded = Math.round(totalInches * 100) / 100;
   const sign = rounded < 0 ? -1 : 1;
   const abs = Math.abs(rounded);
   const feet = Math.floor(abs / 12);
   const inches = Math.round((abs - feet * 12) * 100) / 100;
   return {feet: sign * feet, inches: sign * inches};
}

/**
 * joinGaugeInches: the two box strings -> total inches, or null when both
 * are blank (nothing entered). One box blank counts as 0 in that box, so
 * typing just "6" feet is 72 in. Unparseable input yields null.
 */
export function joinGaugeInches(feet: string, inches: string): number | null {
   if (feet.trim() === '' && inches.trim() === '') return null;
   const f = feet.trim() === '' ? 0 : Number(feet);
   const i = inches.trim() === '' ? 0 : Number(inches);
   if (!Number.isFinite(f) || !Number.isFinite(i)) return null;
   return Math.round((f * 12 + i) * 100) / 100;
}

/** Formats a stored total as e.g. `6 ft 4.5 in` for read-only text. */
export function formatGaugeFeetInches(totalInches: number): string {
   const {feet, inches} = splitGaugeInches(totalInches);
   return `${feet} ft ${inches} in`;
}
